import { useMemo, useState } from "react";
import type { FormEvent } from "react";

import { createApiClient } from "../infrastructure/http/apiClient";
import { brandMarkPath } from "../components/common/BrandLockup";
import { loadAuthenticatedBoot, type AuthenticatedBootContext } from "../modules/appBoot";
import { loginWithPassword } from "../modules/auth";
import { AuthenticatedShellPage } from "./AuthenticatedShellPage";

type LoginState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; boot: AuthenticatedBootContext; accessToken: string }
  | { status: "error"; title: string; message: string; retryable: boolean };

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState<LoginState>({ status: "idle" });

  const publicApiClient = useMemo(() => createApiClient(), []);

  if (state.status === "success") {
    return <AuthenticatedShellPage initialBoot={state.boot} initialAccessToken={state.accessToken} />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!email.trim() || !password) {
      setState({
        status: "error",
        title: "Dados incompletos",
        message: "Informe e-mail e senha para entrar.",
        retryable: false
      });
      return;
    }

    setState({ status: "loading" });

    try {
      const session = await loginWithPassword(publicApiClient, {
        email: email.trim(),
        password
      });

      const authenticatedApiClient = createApiClient({
        getAuthToken: () => session.access_token
      });
      const boot = await loadAuthenticatedBoot(authenticatedApiClient);
      if (typeof window !== "undefined") {
        window.location.assign(getAuthenticatedRoutePath(window.location.pathname));
        return;
      }
      setState({ status: "success", boot, accessToken: session.access_token });
    } catch (error) {
      setState(toLoginError(error));
    } finally {
      setPassword("");
    }
  }

  return (
    <section className="loginflow">
      <div className="loginflow__panel">
        <div className="loginflow__brand" aria-label="THH GO">
          <img className="loginflow__brandMark" src={brandMarkPath} alt="" aria-hidden="true" />
          <span>THH GO</span>
        </div>
        <div className="loginflow__intro">
          <h1>Entrar</h1>
          <span>Acesse sua rotina operacional no THH GO.</span>
        </div>

        <form className="loginflow__form" onSubmit={handleSubmit}>
          <label htmlFor="login-email">E-mail</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <label htmlFor="login-password">Senha</label>
          <div className="loginflow__password">
            <input
              id="login-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button type="button" aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword((current) => !current)}>
              <EyeIcon />
            </button>
          </div>

          <a className="loginflow__forgot" href="/go">Esqueci minha senha</a>

          <button type="submit" disabled={state.status === "loading"}>
            {state.status === "loading" ? "Entrando" : "Entrar"}
          </button>
          <LoginResult state={state} />
          <div className="loginflow__links">
            <span>Não tem conta?</span>
            <a href="/go">Solicitar acesso</a>
          </div>
        </form>
      </div>

      <aside className="loginflow__context" aria-label="THH GO agenda">
        <CalendarIcon />
        <div className="loginflow__contextCopy">
          <h2>Eleve a experiência do cliente e a eficiência da operação</h2>
          <span>Agenda, clientes, profissionais e ações do dia em uma rotina simples, controlada e previsível.</span>
        </div>
      </aside>
    </section>
  );
}

export function getAuthenticatedRoutePath(pathname: string): string {
  return pathname.startsWith("/go/") ? "/go/app" : "/app";
}

function LoginResult({ state }: { state: LoginState }) {
  if (state.status === "idle") {
    return null;
  }

  if (state.status === "loading") {
    return (
      <div className="loginflow__result loginflow__result--muted" aria-live="polite">
        <strong>Validando acesso</strong>
        <span>Estamos preparando seu ambiente de trabalho.</span>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="loginflow__result loginflow__result--error" role="alert">
        <strong>{state.title}</strong>
        <span>{state.message}</span>
        {state.retryable && <small>Tente novamente em instantes.</small>}
      </div>
    );
  }

  return (
    <div className="loginflow__result loginflow__result--muted" aria-live="polite">
      <strong>Sessão validada</strong>
      <span>Seu ambiente interno está sendo preparado com segurança.</span>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg className="loginflow__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="4" y="5.5" width="16" height="15" rx="3" />
      <path d="M8 3.5v4" />
      <path d="M16 3.5v4" />
      <path d="M4 10h16" />
      <path d="m8.5 15 2.4 2.4 4.8-5" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2.5 12s3.5-5 9.5-5 9.5 5 9.5 5-3.5 5-9.5 5-9.5-5-9.5-5Z" />
      <path d="M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
    </svg>
  );
}

function toLoginError(error: unknown): LoginState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return {
        status: "error",
        title: "Credenciais inválidas",
        message: "Confira e-mail e senha para tentar novamente.",
        retryable: false
      };
    }

    if (error.code === "invalid_response") {
      return {
        status: "error",
        title: "Resposta inesperada",
        message: "Não foi possível confirmar sua sessão com segurança.",
        retryable: false
      };
    }

    return {
      status: "error",
      title: "Falha no login",
      message: "Não foi possível concluir a entrada agora.",
      retryable: error.retryable
    };
  }

  return {
    status: "error",
    title: "Falha inesperada",
    message: "Não foi possível concluir a entrada.",
    retryable: true
  };
}

function isApiError(error: unknown): error is { code: string; retryable: boolean } {
  return typeof error === "object"
    && error !== null
    && "code" in error
    && "retryable" in error;
}
